import React, { useState } from "react";
import { useRouter } from "next/router";
import { bool, string } from "prop-types";
import { twMerge } from "tailwind-merge";

import { useTranslation } from "@lib/i18n";

import CheckMarkIcon from "@components/shared/Icons/Checkmark";
import Icon from "@components/shared/Icons/Icon";
import ArrowRightIcon from "@components/shared/Icons/ArrowRight";
import CloseIcon from "@components/shared/Icons/Close";
import Typography from "@components/shared/Typography";
import CEESubsidyDetailsModal from "@components/PriceDetail/CEESubsidyDetailsModal";

import { withTabNavigation } from "@hoc/withTabNavigation";

import { toggleCEESubsidyModalEvent } from "@gtm/events/homepage";
import gtm from "@gtm/core";

import {
  useCEESubsidySelector,
  useCurrencySettingsSelector,
} from "@redux/reducers/appConfig";
import { useIsCEESubsidySelectedSelector } from "@redux/reducers/deal";

import { isOrderSummaryPage } from "@shared/helper/isOrderSummaryPage";
import { getFormattedPrice } from "@shared/v2/lib/getFormattedPrice";

const CLASSNAME = {
  TYPOGRAPHY:
    "inline-block text-left text-primaryDark text-xs tracking-wider uppercase font-extrabold text-balance",
  LINK: "cursor-pointer text-primaryOrange inline-flex items-center gap-1",
};

const LinkTypography = withTabNavigation(Typography);

const CEESubsidyDiscountStripeSection = ({ ceeSubsidyType, disabled }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const isOnSummaryPage = isOrderSummaryPage(router.pathname);

  const ceeSubsidySelected = useIsCEESubsidySelectedSelector();
  const isCEESubsidySelected = ceeSubsidySelected === ceeSubsidyType;
  const ceeSubsidy = useCEESubsidySelector(ceeSubsidyType);
  const isCEESubsidyEnabled = ceeSubsidy?.enabled;

  const [isCEESubsidyModalOpen, setIsCEESubsidyModalOpen] = useState(false);

  const currencySettings = useCurrencySettingsSelector();
  const targetCurrencyFormat = currencySettings.format;
  const targetCurrencySymbol = currencySettings.symbol;

  const openCEESubsidyModal = () => {
    gtm.fire(
      toggleCEESubsidyModalEvent({
        displayClose: "Display",
        label: t(isCEESubsidyEnabled ? "price.deactivate" : "price.clickHere"),
      })
    );

    setIsCEESubsidyModalOpen(true);
  };

  const closeCEESubsidyModal = () => {
    gtm.fire(
      toggleCEESubsidyModalEvent({
        displayClose: "Close",
        label: "close",
      })
    );

    setIsCEESubsidyModalOpen(false);
  };

  if (!isCEESubsidyEnabled) {
    return null;
  }

  return (
    <>
      <div className="flex justify-start items-center gap-2">
        {isOnSummaryPage && !isCEESubsidySelected ? (
          <Icon className="text-greyLight size-6 p-1.5" icon={<CloseIcon />} />
        ) : (
          <CheckMarkIcon
            className={
              isCEESubsidySelected ? "text-primaryOrange" : "text-greyLight"
            }
            checked
            height="26px"
            width="26px"
          />
        )}
        <Typography className={CLASSNAME.TYPOGRAPHY}>
          {getFormattedPrice(
            ceeSubsidy.discountAmount * -1,
            targetCurrencyFormat,
            targetCurrencySymbol
          )}
        </Typography>
        <Typography className={CLASSNAME.TYPOGRAPHY}>
          {t(`price.ceeSubsidy.${ceeSubsidyType}.stripe.title`)}
        </Typography>
        {!disabled && !isOnSummaryPage && (
          <LinkTypography
            className={twMerge(CLASSNAME.TYPOGRAPHY, CLASSNAME.LINK)}
            onClick={openCEESubsidyModal}
          >
            {t(`price.ceeSubsidy.${ceeSubsidyType}.stripe.learnMore`)}
            <Icon className="size-3" icon={<ArrowRightIcon />} />
          </LinkTypography>
        )}
      </div>
      <CEESubsidyDetailsModal
        isOpen={isCEESubsidyModalOpen}
        isCEESubsidySelected={ceeSubsidySelected}
        onClose={() => setIsCEESubsidyModalOpen(false)}
        closeCeeSubsidyModal={closeCEESubsidyModal}
        ceeSubsidyType={ceeSubsidyType}
      />
    </>
  );
};

CEESubsidyDiscountStripeSection.propTypes = {
  ceeSubsidyType: string.isRequired,
  disabled: bool,
};

export default CEESubsidyDiscountStripeSection;
